import { useEffect, useState } from 'react';
import { dismissAlertRequestSchema, type AlertFindingRow } from '@forecastify/shared';
import { Label, Textarea } from '@forecastify/ui';
import { useT } from '../../i18n';
import { EditorDialog } from '../settings/editor-dialog';
import { alertName } from './alert-text';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C59 · P5-5. Waiving a rule on a subject, with a reason and a name on it.  │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE HINT IS NOT DECORATION — IT SAYS WHAT THE READER IS ACTUALLY DOING.** They pressed a
 * kebab on one row; what they get is a standing exception on `(rule, subject)`, which will also
 * suppress this finding when it resolves and comes back. That is the whole of P5-5 and it is
 * invisible from the row, so the dialog says it in a sentence before they commit.
 *
 * ⚠️ **The reason is required, and validated against the SHARED schema.** The same object the
 * server validates with, so the button cannot enable on something the endpoint will refuse.
 */
export function DismissAlertDialog({
  finding,
  sameKind = 0,
  allOnly = false,
  busy = false,
  onClose,
  onConfirm,
}: {
  readonly finding: AlertFindingRow | null;
  /**
   * 26 Oct 2026 · how many ACTIVE findings share this one's kind on the project, itself included.
   * Above one, *Az összes ilyen alert mellőzése (N)* waives them all with the same reason.
   */
  readonly sameKind?: number;
  /**
   * 30 Oct 2026 · **opened from a group row** — the one action is waiving every active finding of
   * the kind, so it is the primary button and there is no single-finding one beside it.
   */
  readonly allOnly?: boolean;
  /** The write is in flight — the dialog stays open, its buttons wait. */
  readonly busy?: boolean;
  readonly onClose: () => void;
  readonly onConfirm: (reason: string, all: boolean) => void;
}) {
  const t = useT();
  const [reason, setReason] = useState('');

  /* The reason is cleared once the dialog closes — after the write, not before it, so a failed
     write keeps what somebody typed. */
  useEffect(() => {
    if (finding === null) setReason('');
  }, [finding]);

  const valid = dismissAlertRequestSchema.safeParse({ reason }).success;

  return (
    <EditorDialog
      open={finding !== null}
      title={t('alerts.dismiss.title')}
      description={finding === null ? undefined : alertName(t, finding.kind)}
      saveLabel={
        allOnly ? t('alerts.dismiss.submitAll', { count: sameKind }) : t('alerts.dismiss.submit')
      }
      saveDisabled={!valid}
      busy={busy}
      {...(sameKind > 1 && !allOnly
        ? {
            extraAction: {
              label: t('alerts.dismiss.submitAll', { count: sameKind }),
              onClick: () => {
                onConfirm(reason.trim(), true);
              },
            },
          }
        : {})}
      onClose={() => {
        setReason('');
        onClose();
      }}
      onSave={() => {
        onConfirm(reason.trim(), allOnly);
      }}
    >
      <div className="flex flex-col gap-3">
        <p className="text-muted-foreground text-[12px] leading-[1.5]">
          {t('alerts.dismiss.hint')}
        </p>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="alert-dismiss-reason">{t('alerts.dismiss.reason')}</Label>
          <Textarea
            id="alert-dismiss-reason"
            rows={3}
            value={reason}
            placeholder={t('alerts.dismiss.reasonPlaceholder')}
            onChange={(event) => {
              setReason(event.target.value);
            }}
          />
          {/* ⚠️ The requirement is stated where it is enforced, not only by a disabled button — a
              control that does nothing and says nothing is `state-rules.md`'s own complaint. */}
          {valid ? null : (
            <p className="text-muted-foreground text-[11px]">
              {t('alerts.dismiss.reasonRequired')}
            </p>
          )}
        </div>
      </div>
    </EditorDialog>
  );
}
